import { Routes } from "@angular/router";

/**
 * Org + campus admin route tree. Mounted under BOTH /admin (customer-facing
 * org admin) and /super/orgs (Classjax-staff inspection). Same components,
 * same URLs sans the parent prefix — components read AdminContextService to
 * know which root to render breadcrumbs / back-links against.
 *
 * Layout (relative to mount point):
 *   ""                              → directory of orgs and campuses (admin only)
 *   orgs/:orgId                     → org admin (settings, branding, pages)
 *   orgs/:orgId/branding
 *   orgs/:orgId/pages
 *   orgs/:orgId/staff
 *   orgs/:orgId/staff/:userId       → read-only user drill-down
 *   orgs/:orgId/students
 *   orgs/:orgId/calendar
 *   orgs/:orgId/audit/assume
 *   orgs/:orgId/campuses/:campusId              → campus admin
 *   orgs/:orgId/campuses/:campusId/staff
 *   orgs/:orgId/campuses/:campusId/students
 *   orgs/:orgId/campuses/:campusId/staff/:userId  → campus-scoped user drill-down
 *   orgs/:orgId/campuses/:campusId/branding
 *   orgs/:orgId/campuses/:campusId/pages
 *   orgs/:orgId/campuses/:campusId/resources
 *   orgs/:orgId/campuses/:campusId/planners
 *   orgs/:orgId/campuses/:campusId/reports
 *   orgs/:orgId/campuses/:campusId/reports/planning-activity
 *   orgs/:orgId/campuses/:campusId/reports/standards-coverage
 *
 * Campus routes are nested under their org so the URL carries the full
 * org → campus → page hierarchy. Each campus component reads campusId
 * (and now orgId) from the route.
 */
export const orgAdminChildRoutes: Routes = [
  // Org admin
  {
    path: "orgs/:orgId",
    loadComponent: () =>
      import("./org-overview/org-overview.component").then(
        (m) => m.OrgOverviewComponent,
      ),
  },
  {
    path: "orgs/:orgId/branding",
    loadComponent: () =>
      import("./org-branding/org-branding.component").then(
        (m) => m.OrgBrandingComponent,
      ),
  },
  {
    path: "orgs/:orgId/pages",
    loadComponent: () =>
      import("./org-pages/org-pages.component").then(
        (m) => m.OrgPagesComponent,
      ),
  },
  {
    path: "orgs/:orgId/staff",
    loadComponent: () =>
      import("./org-staff/org-staff.component").then(
        (m) => m.OrgStaffComponent,
      ),
  },
  {
    path: "orgs/:orgId/students",
    loadComponent: () =>
      import("./org-students/org-students.component").then(
        (m) => m.OrgStudentsComponent,
      ),
  },
  {
    path: "orgs/:orgId/staff/:userId",
    loadComponent: () =>
      import("./org-user-detail/org-user-detail.component").then(
        (m) => m.OrgUserDetailComponent,
      ),
  },
  {
    path: "orgs/:orgId/calendar",
    loadComponent: () =>
      import("./org-calendar/org-calendar.component").then(
        (m) => m.OrgCalendarComponent,
      ),
  },
  {
    path: "orgs/:orgId/audit/assume",
    loadComponent: () =>
      import("./org-assume-audit/org-assume-audit.component").then(
        (m) => m.OrgAssumeAuditComponent,
      ),
  },

  // Campus admin
  {
    path: "orgs/:orgId/campuses/:campusId",
    loadComponent: () =>
      import("../campuses/campus-overview/campus-overview.component").then(
        (m) => m.CampusOverviewComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/staff",
    loadComponent: () =>
      import("../campuses/campus-members/campus-members.component").then(
        (m) => m.CampusMembersComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/students",
    loadComponent: () =>
      import("../campuses/campus-students/campus-students.component").then(
        (m) => m.CampusStudentsComponent,
      ),
  },
  {
    // Campus-scoped user drill-down — same component as the org-scoped one,
    // but keeps the campus context (breadcrumb + back-link). orgId is derived
    // from the campus.
    path: "orgs/:orgId/campuses/:campusId/staff/:userId",
    loadComponent: () =>
      import("./org-user-detail/org-user-detail.component").then(
        (m) => m.OrgUserDetailComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/branding",
    loadComponent: () =>
      import("../campuses/campus-branding/campus-branding.component").then(
        (m) => m.CampusBrandingComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/pages",
    loadComponent: () =>
      import("../campuses/campus-pages/campus-pages.component").then(
        (m) => m.CampusPagesComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/resources",
    loadComponent: () =>
      import("../campuses/campus-resources/campus-resources.component").then(
        (m) => m.CampusResourcesComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/planners",
    loadComponent: () =>
      import("../campuses/campus-planners/campus-planners.component").then(
        (m) => m.CampusPlannersComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/reports",
    loadComponent: () =>
      import("../campuses/campus-reports/campus-reports.component").then(
        (m) => m.CampusReportsComponent,
      ),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/reports/planning-activity",
    loadComponent: () =>
      import(
        "../campuses/campus-reports/planning-activity/planning-activity.component"
      ).then((m) => m.PlanningActivityComponent),
  },
  {
    path: "orgs/:orgId/campuses/:campusId/reports/standards-coverage",
    loadComponent: () =>
      import(
        "../campuses/campus-reports/standards-coverage/standards-coverage.component"
      ).then((m) => m.StandardsCoverageComponent),
  },
];
